import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supportAPI } from '../services/api';
import './HelpSupport.css';

const HelpSupport = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('faq');
  const [supportForm, setSupportForm] = useState({
    subject: '',
    category: '',
    description: '',
    priority: 'medium'
  });
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // My Tickets state
  const [myTickets, setMyTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  const categories = {
    user: ['Orders', 'Payments', 'Account', 'Delivery', 'Refunds', 'Technical', 'Other'],
    restaurant: ['Subscription', 'Menu Management', 'Orders', 'Payments', 'Account', 'Technical', 'Other'],
    delivery: ['Deliveries', 'Payments', 'Account', 'COD Settlement', 'Technical', 'Other'],
    admin: ['System', 'Technical', 'Reports', 'Other']
  };

  const faqs = {
    user: [
      {
        question: 'How do I track my order?',
        answer: 'Go to "My Orders" and click on your active order to see real-time tracking with GPS location of your delivery partner.'
      },
      {
        question: 'How can I cancel my order?',
        answer: 'You can cancel an order within 5 minutes of placing it. Go to "My Orders", select the order, and click "Cancel Order". Refunds are processed within 3-5 business days.'
      },
      {
        question: 'What payment methods are accepted?',
        answer: 'We accept UPI, Credit/Debit Cards, Net Banking, and Cash on Delivery (COD). All online payments are secured through Cashfree.'
      },
      {
        question: 'How do I apply a coupon code?',
        answer: 'On the cart page, you\'ll see available coupons. Click "Apply" on the coupon you want to use. The discount will be reflected in your total amount.'
      },
      {
        question: 'What if I received wrong or missing items?',
        answer: 'Contact support immediately through the "Help" button on your order page. Provide order details and photos of received items. We\'ll process a refund or replacement.'
      },
      {
        question: 'How do I add or change my delivery address?',
        answer: 'Go to your Profile page and manage your addresses in the "Saved Addresses" section. You can add, edit, or delete addresses anytime.'
      }
    ],
    restaurant: [
      {
        question: 'How do I update my menu?',
        answer: 'Go to "Menu Management" to add, edit, or remove items. You can update prices, descriptions, images, and availability status in real-time.'
      },
      {
        question: 'What are the subscription plans?',
        answer: 'We offer two plans: One-Time Fee (₹50,000 with ₹10,000 upfront, 60-day trial) and Commission-Based (10% per order). Choose based on your expected order volume.'
      },
      {
        question: 'How do I accept or reject orders?',
        answer: 'New orders appear in your "Orders" section. You have 5 minutes to accept or reject. Auto-rejection happens after timeout. Keep notifications enabled.'
      },
      {
        question: 'When do I receive payments?',
        answer: 'For One-Time Fee: Weekly settlements. For Commission-Based: Weekly settlements minus 10% commission. All transactions are tracked in your dashboard.'
      },
      {
        question: 'Can I temporarily close my restaurant?',
        answer: 'Yes, use the "Temporarily Closed" toggle in your Profile page. This prevents new orders while keeping your listing active.'
      },
      {
        question: 'How do I create coupon codes?',
        answer: 'Go to "Coupon Management" to create discount codes. Set discount type (percentage/fixed), minimum order value, validity period, and usage limits.'
      }
    ],
    delivery: [
      {
        question: 'How do I accept delivery orders?',
        answer: 'Check "Available Orders" for nearby deliveries. Click "Accept" to take the order. You\'ll see pickup and delivery addresses with navigation.'
      },
      {
        question: 'How does COD settlement work?',
        answer: 'Collect cash from customers for COD orders. At the end of your shift, go to "COD Settlement" to submit collected amount via UPI. Upload payment screenshot for verification.'
      },
      {
        question: 'How are delivery fees calculated?',
        answer: 'Delivery fees are based on distance and order value. You receive the full delivery fee plus any tips from customers. View earnings in your dashboard.'
      },
      {
        question: 'What if customer is not available?',
        answer: 'Try calling the customer. Wait for 10 minutes at the location. If still unavailable, contact support through the order page. Follow instructions for undelivered orders.'
      },
      {
        question: 'How do I update my availability?',
        answer: 'Toggle "Available for Deliveries" in your Profile or Dashboard. Update your work areas to receive orders only from specified zones.'
      },
      {
        question: 'When do I get paid?',
        answer: 'Earnings are settled weekly to your registered bank account. View detailed earnings breakdown in your Profile under "Earnings History".'
      }
    ],
    admin: [
      {
        question: 'How do I approve restaurant onboarding?',
        answer: 'Go to "Approvals" section, review restaurant details including documents, location, and subscription plan. Approve or reject with comments.'
      },
      {
        question: 'How do I manage work areas?',
        answer: 'Use "Work Area Management" to create delivery zones. Draw polygons on the map to define areas. Assign delivery partners to specific zones.'
      },
      {
        question: 'How do I handle subscription changes?',
        answer: 'View all subscription change requests in "Subscription Management". Approve or reject based on payment verification and restaurant performance.'
      },
      {
        question: 'How do I block accounts?',
        answer: 'Go to "Account Management", select the account type (User/Restaurant/Delivery), click on account, and use "Block" action with a reason.'
      }
    ]
  };

  const quickGuides = {
    user: [
      {
        title: '🛒 How to Place an Order',
        steps: [
          'Browse restaurants on the Home page',
          'Select a restaurant and add items to cart',
          'Review cart and apply coupons if available',
          'Choose delivery address or add new one',
          'Select payment method and complete payment',
          'Track your order in "My Orders" section'
        ]
      },
      {
        title: '📍 How to Add Delivery Address',
        steps: [
          'Go to Profile page',
          'Click "Add New Address"',
          'Enter address details or use current location',
          'Mark as Home, Work, or Other',
          'Save address for future orders'
        ]
      }
    ],
    restaurant: [
      {
        title: '🏪 Restaurant Onboarding',
        steps: [
          'Register as a restaurant owner',
          'Complete business details and upload documents',
          'Add restaurant location and operating hours',
          'Select subscription plan (One-Time or Commission)',
          'Make initial payment for One-Time Fee plan',
          'Wait for admin approval (24-48 hours)',
          'Start accepting orders after approval'
        ]
      },
      {
        title: '📋 Managing Orders',
        steps: [
          'Enable notifications to receive new orders',
          'Review order details when notification arrives',
          'Accept order within 5 minutes',
          'Update order status as you prepare food',
          'Mark as ready when food is prepared',
          'Delivery partner will pick up the order'
        ]
      }
    ],
    delivery: [
      {
        title: '🚴 Getting Started as Delivery Partner',
        steps: [
          'Register as delivery partner',
          'Complete profile with vehicle details',
          'Upload required documents (license, ID proof)',
          'Pay onboarding fee (₹500)',
          'Wait for admin approval',
          'Enable location sharing and go online',
          'Start accepting delivery orders'
        ]
      },
      {
        title: '💰 COD Settlement Process',
        steps: [
          'Complete all COD deliveries',
          'Go to "COD Settlement" page',
          'Enter total collected cash amount',
          'Make payment via UPI to company account',
          'Upload payment screenshot',
          'Wait for admin verification',
          'Settlement marked as completed'
        ]
      }
    ],
    admin: [
      {
        title: '✅ Approving Restaurant Applications',
        steps: [
          'Go to "Approvals" section',
          'Review restaurant details and documents',
          'Verify business registration and FSSAI license',
          'Check location and operating hours',
          'Verify subscription payment if applicable',
          'Approve or reject with reason',
          'Send notification to restaurant owner'
        ]
      }
    ]
  };

  const contactInfo = {
    email: 'support@foodwinc.com',
    phone: '+91 98765 43210',
    hours: '24/7 Support Available',
    address: 'Foodwinc Technologies Pvt. Ltd., Bangalore, India'
  };

  const handleFormChange = (e) => {
    setSupportForm({
      ...supportForm,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();

    if (!supportForm.subject || !supportForm.category || !supportForm.description) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      return;
    }

    setTicketSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await supportAPI.createTicket(supportForm);

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Support ticket created successfully! Ticket ID: #${response.data.data.ticketId}`
        });
        setSupportForm({
          subject: '',
          category: '',
          description: '',
          priority: 'medium'
        });
        // Refresh tickets if on My Tickets tab
        if (activeTab === 'mytickets') {
          fetchMyTickets();
        }
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to create ticket' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to submit support ticket. Please try again.' });
    } finally {
      setTicketSubmitting(false);
    }
  };

  // Fetch user's tickets
  useEffect(() => {
    if (activeTab === 'mytickets') {
      fetchMyTickets();
    }
  }, [activeTab, ticketFilter]);

  const fetchMyTickets = async () => {
    setTicketsLoading(true);
    try {
      const params = ticketFilter ? { status: ticketFilter } : {};
      const response = await supportAPI.getMyTickets(params);
      setMyTickets(response.data.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const response = await supportAPI.getTicketById(ticketId);
      setSelectedTicket(response.data.data.ticket);
      setReplyText('');
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
      alert('Failed to load ticket details');
    }
  };

  const handleAddReply = async () => {
    if (!replyText.trim()) {
      alert('Please enter a reply');
      return;
    }

    setReplySubmitting(true);
    try {
      await supportAPI.addResponse(selectedTicket.ticketId, { message: replyText });
      setReplyText('');
      // Refresh ticket details
      handleViewTicket(selectedTicket.ticketId);
      // Refresh ticket list
      fetchMyTickets();
    } catch (error) {
      console.error('Failed to add reply:', error);
      alert('Failed to send reply');
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) {
      return;
    }

    try {
      await supportAPI.closeTicket(ticketId);
      alert('Ticket closed successfully');
      setSelectedTicket(null);
      fetchMyTickets();
    } catch (error) {
      console.error('Failed to close ticket:', error);
      alert('Failed to close ticket');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: '#ffa500',
      in_progress: '#4ecdc4',
      resolved: '#51cf66',
      closed: '#666'
    };
    return colors[status] || '#666';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#51cf66',
      medium: '#ffa500',
      high: '#ff6b6b',
      urgent: '#cc0000'
    };
    return colors[priority] || '#666';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const userRole = user?.role || 'user';
  const userFaqs = faqs[userRole] || faqs.user;
  const userGuides = quickGuides[userRole] || quickGuides.user;
  const userCategories = categories[userRole] || categories.user;

  return (
    <div className="help-support">
      <div className="help-header">
        <h1>❓ Help & Support</h1>
        <p>Find answers to common questions or submit a support ticket</p>
      </div>

      <div className="help-tabs">
        <button
          className={`help-tab ${activeTab === 'faq' ? 'active' : ''}`}
          onClick={() => setActiveTab('faq')}
        >
          📚 FAQs
        </button>
        <button
          className={`help-tab ${activeTab === 'guides' ? 'active' : ''}`}
          onClick={() => setActiveTab('guides')}
        >
          📖 Quick Guides
        </button>
        <button
          className={`help-tab ${activeTab === 'mytickets' ? 'active' : ''}`}
          onClick={() => setActiveTab('mytickets')}
        >
          🎫 My Tickets
        </button>
        <button
          className={`help-tab ${activeTab === 'ticket' ? 'active' : ''}`}
          onClick={() => setActiveTab('ticket')}
        >
          ✉️ Submit Ticket
        </button>
        <button
          className={`help-tab ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          📞 Contact Us
        </button>
      </div>

      <div className="help-content">
        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="faq-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              {userFaqs.map((faq, index) => (
                <details key={index} className="faq-item">
                  <summary className="faq-question">
                    {faq.question}
                  </summary>
                  <div className="faq-answer">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Quick Guides Tab */}
        {activeTab === 'guides' && (
          <div className="guides-section">
            <h2>Quick Guides</h2>
            <div className="guides-grid">
              {userGuides.map((guide, index) => (
                <div key={index} className="guide-card">
                  <h3>{guide.title}</h3>
                  <ol className="guide-steps">
                    {guide.steps.map((step, stepIndex) => (
                      <li key={stepIndex}>{step}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Us Tab */}
        {activeTab === 'contact' && (
          <div className="contact-section">
            <h2>Contact Information</h2>
            <div className="contact-grid">
              <div className="contact-card">
                <div className="contact-icon">📧</div>
                <h3>Email Support</h3>
                <p>{contactInfo.email}</p>
                <small>We respond within 24 hours</small>
              </div>
              <div className="contact-card">
                <div className="contact-icon">📱</div>
                <h3>Phone Support</h3>
                <p>{contactInfo.phone}</p>
                <small>{contactInfo.hours}</small>
              </div>
              <div className="contact-card">
                <div className="contact-icon">🏢</div>
                <h3>Office Address</h3>
                <p>{contactInfo.address}</p>
                <small>Visit us during business hours</small>
              </div>
            </div>

            <div className="emergency-contact">
              <h3>🚨 Emergency Support</h3>
              <p>For urgent issues like order problems, delivery delays, or payment issues:</p>
              <ul>
                <li>Call our 24/7 helpline: <strong>{contactInfo.phone}</strong></li>
                <li>Use the "Help" button on your active order page</li>
                <li>WhatsApp support: <strong>+91 98765 43210</strong></li>
              </ul>
            </div>
          </div>
        )}

        {/* My Tickets Tab */}
        {activeTab === 'mytickets' && (
          <div className="mytickets-section">
            <div className="mytickets-header">
              <h2>My Support Tickets</h2>
              <div className="ticket-filter">
                <label>Filter by Status:</label>
                <select
                  value={ticketFilter}
                  onChange={(e) => setTicketFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Tickets</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {ticketsLoading ? (
              <div className="loading">Loading tickets...</div>
            ) : myTickets.length === 0 ? (
              <div className="no-tickets">
                <p>You haven't submitted any support tickets yet.</p>
                <button
                  className="btn-submit-ticket"
                  onClick={() => setActiveTab('ticket')}
                >
                  Submit Your First Ticket
                </button>
              </div>
            ) : (
              <div className="tickets-list">
                {myTickets.map((ticket) => (
                  <div
                    key={ticket._id}
                    className="ticket-item"
                    onClick={() => handleViewTicket(ticket.ticketId)}
                  >
                    <div className="ticket-item-header">
                      <span className="ticket-item-id">#{ticket.ticketId}</span>
                      <span
                        className="ticket-item-status"
                        style={{ background: getStatusColor(ticket.status) }}
                      >
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span
                        className="ticket-item-priority"
                        style={{ background: getPriorityColor(ticket.priority) }}
                      >
                        {ticket.priority.toUpperCase()}
                      </span>
                    </div>
                    <h3 className="ticket-item-subject">{ticket.subject}</h3>
                    <div className="ticket-item-meta">
                      <span>📁 {ticket.category}</span>
                      <span>📅 {formatDate(ticket.createdAt)}</span>
                      <span>💬 {ticket.responses?.length || 0} replies</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Ticket Detail Modal */}
            {selectedTicket && (
              <div className="ticket-modal-overlay" onClick={() => setSelectedTicket(null)}>
                <div className="ticket-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="ticket-modal-header">
                    <h2>Ticket #{selectedTicket.ticketId}</h2>
                    <button
                      className="modal-close-btn"
                      onClick={() => setSelectedTicket(null)}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="ticket-modal-body">
                    <div className="ticket-details">
                      <div className="detail-row">
                        <strong>Subject:</strong>
                        <span>{selectedTicket.subject}</span>
                      </div>
                      <div className="detail-row">
                        <strong>Status:</strong>
                        <span
                          className="status-badge"
                          style={{ background: getStatusColor(selectedTicket.status) }}
                        >
                          {selectedTicket.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Priority:</strong>
                        <span
                          className="priority-badge"
                          style={{ background: getPriorityColor(selectedTicket.priority) }}
                        >
                          {selectedTicket.priority.toUpperCase()}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Category:</strong>
                        <span>{selectedTicket.category}</span>
                      </div>
                      <div className="detail-row">
                        <strong>Created:</strong>
                        <span>{formatDate(selectedTicket.createdAt)}</span>
                      </div>
                      <div className="detail-row full-width">
                        <strong>Description:</strong>
                        <p>{selectedTicket.description}</p>
                      </div>
                    </div>

                    <div className="ticket-conversation">
                      <h3>Conversation History</h3>
                      <div className="conversation-list">
                        {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                          selectedTicket.responses.map((response, index) => (
                            <div
                              key={index}
                              className={`conversation-message ${
                                response.responderRole === 'admin' ? 'admin-message' : 'user-message'
                              }`}
                            >
                              <div className="message-header">
                                <strong>{response.responderName}</strong>
                                <span className="message-role">
                                  {response.responderRole === 'admin' ? '🛡️ Support Team' : '👤 You'}
                                </span>
                                <span className="message-time">{formatDate(response.createdAt)}</span>
                              </div>
                              <div className="message-content">{response.message}</div>
                            </div>
                          ))
                        ) : (
                          <div className="no-responses">
                            No responses yet. Our support team will respond within 24 hours.
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedTicket.status !== 'closed' && (
                      <div className="ticket-reply">
                        <h3>Add Reply</h3>
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your message here..."
                          rows="4"
                          className="reply-textarea"
                        />
                        <div className="reply-actions">
                          <button
                            className="btn-send-reply"
                            onClick={handleAddReply}
                            disabled={replySubmitting || !replyText.trim()}
                          >
                            {replySubmitting ? 'Sending...' : '📤 Send Reply'}
                          </button>
                          {selectedTicket.status !== 'closed' && (
                            <button
                              className="btn-close-ticket"
                              onClick={() => handleCloseTicket(selectedTicket.ticketId)}
                            >
                              🔒 Close Ticket
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedTicket.status === 'closed' && (
                      <div className="ticket-closed-message">
                        This ticket has been closed. If you need further assistance, please submit a new ticket.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Submit Ticket Tab */}
        {activeTab === 'ticket' && (
          <div className="ticket-section">
            <h2>Submit Support Ticket</h2>
            <p className="ticket-description">
              Can't find an answer? Submit a support ticket and our team will get back to you within 24 hours.
            </p>

            {message.text && (
              <div className={`ticket-message ${message.type}`}>
                {message.text}
              </div>
            )}

            <form className="ticket-form" onSubmit={handleSubmitTicket}>
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={supportForm.subject}
                  onChange={handleFormChange}
                  placeholder="Brief description of your issue"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={supportForm.category}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select category</option>
                    {userCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <select
                    name="priority"
                    value={supportForm.priority}
                    onChange={handleFormChange}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={supportForm.description}
                  onChange={handleFormChange}
                  placeholder="Provide detailed information about your issue..."
                  rows="6"
                  required
                />
              </div>

              <button
                type="submit"
                className="submit-ticket-btn"
                disabled={ticketSubmitting}
              >
                {ticketSubmitting ? 'Submitting...' : '📨 Submit Ticket'}
              </button>
            </form>

            <div className="ticket-info">
              <h3>What happens next?</h3>
              <ul>
                <li>You'll receive a ticket confirmation email</li>
                <li>Our support team will review your ticket within 24 hours</li>
                <li>You'll get updates via email and app notifications</li>
                <li>Track your ticket status in your Profile section</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpSupport;
